import { ToolDefinition, ToolHandler } from "../types";
import {
  sqlToolDefinitions,
  executeGetDatabaseSchema,
  executeSql,
} from "./sql";
import { runEventToolDefinitions, executeGetRunEvents } from "./runEvents";
import { navigationToolDefinitions, executeNavigateTo } from "./navigation";
import { pythonToolDefinitions, executePython } from "./python";
import { skillToolDefinitions, executeReadSkill } from "./skills";
import {
  memoryToolDefinitions,
  executeAddMemory,
  executeListMemories,
  executeDeleteMemory,
} from "./memories";

/**
 * Central tool registry. The system prompt advertises this list to Claude;
 * the tool dispatcher uses the same names to route execution.
 *
 * Order matters loosely — Claude tends to favor earlier-listed tools when
 * they look equally applicable. We put SQL first because it's the workhorse
 * for analytical questions, then per-run helpers (events), then skills (so
 * Kate considers a curated playbook before improvising), then the rest.
 */
export const TELEMETRY_TOOLS: ToolDefinition[] = [
  ...sqlToolDefinitions,
  ...runEventToolDefinitions,
  ...skillToolDefinitions,
  ...memoryToolDefinitions,
  ...navigationToolDefinitions,
  ...pythonToolDefinitions,
];

const dispatchTable: Record<string, (input: Record<string, unknown>) => Promise<unknown>> = {
  get_database_schema: executeGetDatabaseSchema,
  execute_sql: executeSql,
  get_run_events: executeGetRunEvents,
  read_skill: executeReadSkill,
  add_memory: executeAddMemory,
  list_memories: executeListMemories,
  delete_memory: executeDeleteMemory,
  navigate_to: executeNavigateTo,
  execute_python: executePython,
};

export const dispatchTool: ToolHandler = async (toolName, toolInput) => {
  const handler = dispatchTable[toolName];
  if (!handler) {
    return { error: `Unknown tool: ${toolName}` };
  }
  try {
    return await handler(toolInput);
  } catch (err) {
    return {
      error: `Tool '${toolName}' threw: ${(err as Error).message}`,
    };
  }
};

export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  get_database_schema: "Reading schema",
  execute_sql: "Querying database",
  get_run_events: "Reading run events",
  read_skill: "Loading skill",
  add_memory: "Saving memory",
  list_memories: "Reading memories",
  delete_memory: "Deleting memory",
  navigate_to: "Navigating",
  execute_python: "Running Python",
  // Pseudo-name used by aiService when forcing a final summary after the
  // tool-use loop hits its cap. Surfaced via onToolStart so the user sees
  // it in the status history.
  __force_summary__: "Wrapping up...",
};
